import {
  Backpack,
  Coins,
  Hammer,
  Leaf,
  ListChecks,
  Swords,
  Users,
  WandSparkles,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { APIClient } from "../api";
import { type EffectChannel, type EffectSettings } from "../effects";
import { ShopScreen } from "../ShopScreen";
import { type ThemeId } from "../theme";
import {
  type AutoSellQuality,
  type ConvertibleResource,
  type EquipmentSlot,
  type GrowthProfile,
  type InventoryView,
  type LifeProfile,
  type LifeStartRequest,
  type MeResponse,
  type OfflineAutomationSettings,
  type OfflineQuotaSource,
  type RedeemResult,
  type ResourceConversionResult,
  type RoomListing,
  type SocialProfile,
  type TaskBoard,
} from "../types";
import { type GuildActions, type HubTab } from "../ui/navigation";
import { PageSections } from "../ui/PageSections";
import { DiveScreen } from "./DiveScreen";
import { EquipmentScreen } from "./EquipmentScreen";
import { LifeScreen } from "./LifeScreen";
import { ProfileScreen } from "./ProfileScreen";
import { SkillsScreen } from "./SkillsScreen";
import { WorkshopScreen } from "./WorkshopScreen";

export function HubScreen({
  onShopChanged,
  api,
  tab,
  onTab,
  me,
  inventory,
  growth,
  life,
  clockNow,
  social,
  tasks,
  busy,
  onStart,
  onHeal,
  onClaimOffline,
  onPurchaseOffline,
  onOfflineSettings,
  onMatchRaid,
  onSoloRaid,
  onCreateDuo,
  onJoinRoom,
  onListRooms,
  onStartRace,
  onEquip,
  onUnequip,
  onLock,
  onEnhance,
  onReforge,
  onRefine,
  onCalibrate,
  onSell,
  onSaveSkills,
  onUnlockSkill,
  onRespecSkills,
  onUpgradeWorkshop,
  onConvertWorkshop,
  onStartLife,
  onClaimLife,
  onStopLife,
  onRefreshLife,
  lifeEnhancementItemId,
  onRedeem,
  onClaimTask,
  onClaimAllTasks,
  onFriendRequest,
  onFriendRespond,
  onFriendRemove,
  onCrewCreate,
  onCrewJoin,
  onCrewLeave,
  onCrewSupply,
  guildActions,
  theme,
  fontScale,
  effectSettings,
  muted,
  colorBlind,
  onTheme,
  onFontScale,
  onEffectChannel,
  onToggleMuted,
  onToggleColorBlind,
  onAutoSellQuality,
  onAccount,
  onAdmin,
}: {
  onShopChanged: () => Promise<void>;
  api: APIClient;
  tab: HubTab;
  onTab: (tab: HubTab) => void;
  me: MeResponse;
  inventory: InventoryView;
  growth: GrowthProfile;
  life: LifeProfile;
  clockNow: () => number;
  social: SocialProfile | null;
  tasks: TaskBoard | null;
  busy: boolean;
  onStart: (shaftDepth: number, useFreeSkip: boolean) => void;
  onHeal: () => void;
  onClaimOffline: () => void;
  onPurchaseOffline: (packageId: string) => void;
  onOfflineSettings: (source: OfflineQuotaSource, automation?: OfflineAutomationSettings) => void;
  onMatchRaid: () => void;
  onSoloRaid: () => void;
  onCreateDuo: () => void;
  onJoinRoom: (code: string) => void;
  onStartRace: (kind: "daily" | "weekly") => void;
  onListRooms: (mode: "raid" | "duo" | "") => Promise<RoomListing[]>;
  onEquip: (itemId: string) => void;
  onUnequip: (slot: EquipmentSlot) => void;
  onLock: (itemId: string, locked: boolean) => void;
  onEnhance: (itemId: string) => void;
  onReforge: (itemId: string, affixIndex: number) => void;
  onRefine: (itemId: string, affixIndex: number) => void;
  onCalibrate: (itemId: string) => void;
  onSell: (itemIds: string[]) => void;
  onSaveSkills: (skills: string[]) => void;
  onUnlockSkill: (skillId: string) => void;
  onRespecSkills: () => void;
  onUpgradeWorkshop: (buildingId: string) => void;
  onConvertWorkshop: (
    sourceCurrency: ConvertibleResource,
    targetCurrency: ConvertibleResource,
    sourceAmount: number,
  ) => Promise<ResourceConversionResult>;
  onStartLife: (request: LifeStartRequest) => void;
  onClaimLife: () => void;
  onStopLife: () => void;
  onRefreshLife: () => Promise<void>;
  lifeEnhancementItemId: string;
  onRedeem: (code: string) => Promise<RedeemResult>;
  onClaimTask: (taskId: string) => void;
  onClaimAllTasks: () => void;
  onFriendRequest: (username: string) => void;
  onFriendRespond: (userId: string, accept: boolean) => void;
  onFriendRemove: (userId: string) => void;
  onCrewCreate: (name: string) => void;
  onCrewJoin: (name: string) => void;
  onCrewLeave: () => void;
  onCrewSupply: () => void;
  guildActions: GuildActions;
  theme: ThemeId;
  fontScale: number;
  effectSettings: EffectSettings;
  muted: boolean;
  colorBlind: boolean;
  onTheme: (theme: ThemeId) => void;
  onFontScale: (scale: number) => void;
  onEffectChannel: (channel: EffectChannel, patch: Partial<EffectSettings[EffectChannel]>) => void;
  onToggleMuted: () => void;
  onToggleColorBlind: () => void;
  onAutoSellQuality: (quality: AutoSellQuality) => void;
  onAccount: () => void;
  onAdmin: () => void;
}) {
  const shellRef = useRef<HTMLElement>(null);
  const [lifeTab, setLifeTab] = useState("profession");
  const [workshopTab, setWorkshopTab] = useState("buildings");
  useEffect(() => {
    if (lifeEnhancementItemId) setLifeTab("profession");
  }, [lifeEnhancementItemId]);
  useEffect(() => {
    // Some desktop browsers return a Promise from scrollTo; never expose it as an effect cleanup.
    shellRef.current?.scrollTo({ top: 0 });
  }, [tab]);
  return (
    <main className="hub-shell" ref={shellRef}>
      <div className="hub-content">
        {tab === "life" && (
          <PageSections
            label="生活页面"
            value={lifeTab}
            onChange={setLifeTab}
            items={[
              { id: "profession", label: "生活", icon: <Leaf size={17} /> },
              { id: "tasks", label: "任务", icon: <ListChecks size={17} /> },
              { id: "social", label: "社交", icon: <Users size={17} /> },
            ]}
          />
        )}
        {tab === "workshop" && (
          <PageSections
            label="工坊页面"
            value={workshopTab}
            onChange={setWorkshopTab}
            items={[
              { id: "buildings", label: "工坊", icon: <Hammer size={17} /> },
              { id: "shop", label: "商店", icon: <Coins size={17} /> },
            ]}
          />
        )}
        {tab === "workshop" && workshopTab === "shop" && (
          <ShopScreen api={api} onChanged={onShopChanged} />
        )}
        {tab === "equipment" && (
          <EquipmentScreen
            inventory={inventory}
            goldCap={me.player.storageCaps?.gold ?? Number.MAX_SAFE_INTEGER}
            busy={busy}
            onEquip={onEquip}
            onUnequip={onUnequip}
            onLock={onLock}
            onEnhance={onEnhance}
            onReforge={onReforge}
            onRefine={onRefine}
            onCalibrate={onCalibrate}
            onSell={onSell}
          />
        )}
        {tab === "skills" && (
          <SkillsScreen
            growth={growth}
            busy={busy}
            onSave={onSaveSkills}
            onUnlock={onUnlockSkill}
            onRespec={onRespecSkills}
          />
        )}
        {tab === "battle" && (
          <DiveScreen
            api={api}
            social={social}
            me={me}
            inventory={inventory}
            growth={growth}
            busy={busy}
            onStart={onStart}
            onHeal={onHeal}
            onClaimOffline={onClaimOffline}
            onPurchaseOffline={onPurchaseOffline}
            onOfflineSettings={onOfflineSettings}
            onMatchRaid={onMatchRaid}
            onSoloRaid={onSoloRaid}
            onCreateDuo={onCreateDuo}
            onJoinRoom={onJoinRoom}
            onListRooms={onListRooms}
            onStartRace={onStartRace}
          />
        )}
        {tab === "life" && lifeTab === "profession" && (
          <LifeScreen
            profile={life}
            inventory={inventory}
            me={me}
            clockNow={clockNow}
            busy={busy}
            initialItemId={lifeEnhancementItemId}
            onStart={onStartLife}
            onClaim={onClaimLife}
            onStop={onStopLife}
            onRefresh={onRefreshLife}
          />
        )}
        {tab === "workshop" && workshopTab === "buildings" && (
          <WorkshopScreen
            growth={growth}
            busy={busy}
            onUpgrade={onUpgradeWorkshop}
            onConvert={onConvertWorkshop}
            onRedeem={onRedeem}
          />
        )}
        {(tab === "profile" || (tab === "life" && lifeTab !== "profession")) && (
          <ProfileScreen
            sectionOverride={tab === "life" ? lifeTab : undefined}
            me={me}
            inventory={inventory}
            growth={growth}
            social={social}
            tasks={tasks}
            busy={busy}
            theme={theme}
            fontScale={fontScale}
            effectSettings={effectSettings}
            muted={muted}
            colorBlind={colorBlind}
            onTheme={onTheme}
            onFontScale={onFontScale}
            onEffectChannel={onEffectChannel}
            onToggleMuted={onToggleMuted}
            onToggleColorBlind={onToggleColorBlind}
            onAutoSellQuality={onAutoSellQuality}
            onClaimTask={onClaimTask}
            onClaimAllTasks={onClaimAllTasks}
            onFriendRequest={onFriendRequest}
            onFriendRespond={onFriendRespond}
            onFriendRemove={onFriendRemove}
            onCrewCreate={onCrewCreate}
            onCrewJoin={onCrewJoin}
            onCrewLeave={onCrewLeave}
            onCrewSupply={onCrewSupply}
            guildActions={guildActions}
            onAccount={onAccount}
            onAdmin={onAdmin}
          />
        )}
      </div>
      <nav className="hub-navigator" aria-label="局外导航">
        <button
          className={tab === "equipment" ? "is-active" : ""}
          aria-current={tab === "equipment" ? "page" : undefined}
          onClick={() => onTab("equipment")}
        >
          <Backpack size={21} />
          <span>装备</span>
        </button>
        <button
          className={tab === "skills" ? "is-active" : ""}
          aria-current={tab === "skills" ? "page" : undefined}
          onClick={() => onTab("skills")}
        >
          <WandSparkles size={21} />
          <span>技能</span>
        </button>
        <button
          className={`nav-battle${tab === "battle" ? " is-active" : ""}`}
          aria-current={tab === "battle" ? "page" : undefined}
          onClick={() => onTab("battle")}
        >
          <Swords size={25} />
          <span>战斗</span>
        </button>
        <button
          className={tab === "life" ? "is-active" : ""}
          aria-current={tab === "life" ? "page" : undefined}
          onClick={() => onTab("life")}
        >
          <Leaf size={21} />
          <span>生活</span>
        </button>
        <button
          className={tab === "workshop" ? "is-active" : ""}
          aria-current={tab === "workshop" ? "page" : undefined}
          onClick={() => onTab("workshop")}
        >
          <Hammer size={21} />
          <span>工坊</span>
        </button>
      </nav>
    </main>
  );
}
